import React, { useEffect, useMemo, useRef, useState } from 'react'
import { StyleSheet, TouchableOpacity } from 'react-native';

import {
  DNABox,
  DNADivider,
  DNAText,
  GenericToast,
  Iffy,
  ToastOrientations,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useToast from '@alucio/lux-ui/lib/components/Toast/useToast';

import {
  AssociatedFileStatus,
  AssociatedFileType,
  AttachedFile,
  CustomFieldUsage,
  EmailTemplate,
  EmailTemplateType,
  EmailTemplateStatus,
  FieldDataType,
  ShareType,
} from '@alucio/aws-beacon-amplify/src/models';

import {
  AssociatedFileORM,
  DocumentORM,
  EmailTemplateORM,
  getAssociatedFileTitle,
  ORMTypes,
} from 'src/types/orms';

// redux
import { useDispatch } from 'src/state/redux';
import { useUserTenant } from 'src/state/redux/selector/user';
import { useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { useTemplateById } from 'src/state/redux/selector/emailTemplate';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { createAssociatedFile } from 'src/state/redux/slice/documentVersion';
import { DRAWER_ENTITIES, drawerActions } from 'src/state/redux/slice/drawer';
import { emailTemplateActions } from 'src/state/redux/slice/emailTemplate';
import { fields } from 'src/hooks/useEmailTemplateFormConfig/EmailTemplateConfig';
import {
  getAssociatedFileORMFromFile,
  useAllDocumentsInstance,
} from 'src/state/redux/selector/document';

import { emailTemplatePreview } from 'src/utils/shareLink/shareLink.web';

// Components
import {
  useComposableForm,
} from 'src/components/CustomFields/ComposableForm';
import EmailDrawerFooter from 'src/components/EmailTemplateDrawer/EmailTemplateDrawerUI/EmailDrawerFooter';
import EmailDrawerHeader from 'src/components/EmailTemplateDrawer/EmailTemplateDrawerUI/EmailDrawerHeader';
import DNAEmailTemplateDeleteModal from 'src/components/DNA/Modal/DNAEmailTemplateDeleteModal';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';
import { UploadStatus, UPLOAD_STATUS } from 'src/components/DNA/FileUpload/FileUpload';
import {
  BODY_PLACEHOLDERS,
  BRACKETS_HELP,
  EMAIL_PLACEHOLDER,
  getFormattedLabelValues,
  isStringArray,
  PERFORMING_ACTION,
  SUBMITTING_STATUS,
} from 'src/components/EmailTemplateDrawer/EmailDrawerHelper';
import { HelpCircleTooltip } from 'src/components/DNA';
import { ComposableField } from 'src/components/CustomFields/ComposableField';
import AssociatedFilesManager from 'src/components/DNA/AssociatedFiles/AssociatedFilesManager';
import InputComponent from 'src/components/Publishers/InputComponent';
import { Controller } from 'react-hook-form';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

const BODY_HTML_TEMPLATE = 'Paste the message from your clipboard here. (Ctrl-V)'

/* TYPES */
interface EmailTemplateBodyProps {
  templateId?: string,
  toggleDrawer: () => void,
}

const styles = StyleSheet.create({
  commonPadding: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  inputStyle: {
    backgroundColor: colors['color-text-white'],
    borderColor: colors['color-gray-100'],
  },
});

const USER_FILTER_EMAIL_TEMPLATE_CONFIG = {
  usages: { internalUsages: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER, CustomFieldUsage.USER_FILTER] },
  fieldDataType: [FieldDataType.MULTICATEGORICAL],
}

const EmailTemplateBody = (props: EmailTemplateBodyProps) => {
  const tenant = useUserTenant();
  const allDocumentORM = useAllDocumentsInstance()
  const emailTemplateORM = useTemplateById(props.templateId);
  const performingAction = useRef<PERFORMING_ACTION>();
  const dispatch = useDispatch();
  const [submittingStatus, setSubmittingStatus] = useState<SUBMITTING_STATUS>(SUBMITTING_STATUS.NONE);
  const customFields = useTenantCustomFields(USER_FILTER_EMAIL_TEMPLATE_CONFIG);

  const bodyInputStartCursor = useRef(0);
  const bodyInputEndCursor = useRef(0);
  const lostBodyInputFocusAt = useRef((new Date()).getTime());
  const toast = useToast();

  // This state ORM will be used to work with attached files, using a dummy when we are
  // creating a new email template
  const [latestEmailTemplateORM, setLatestEmailTemplateORM] = useState<EmailTemplateORM>({
    model: emailTemplateORM
      ? emailTemplateORM.model
      : {
        id: '',
        tenantId: '',
        status: EmailTemplateStatus.DRAFT,
        createdBy: '',
        updatedBy: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        type: EmailTemplateType.PLAIN_TEXT,
      },
    relations: {
      associatedFiles: emailTemplateORM ? [...emailTemplateORM.relations.associatedFiles] : [],
    },
    meta: {
      customFilterValues: {},
    },
    type: ORMTypes.EMAIL_TEMPLATE,
  })
  const [searching, setSearching] = useState<boolean>(false);
  const [processingAttachedFiles, setProcessingAttachedFiles] = useState<boolean>(false)

  const activeAssociatedFiles = latestEmailTemplateORM
    .relations
    .associatedFiles
    .filter(af => af.model.status === 'ACTIVE')

  const { rhForm } = useComposableForm()
  const featureFlags = useFeatureFlags();
  const isHtmlTemplateEnabled = featureFlags.BEAC_6984_email_template_type_selector;

  const {
    getValues,
    formState,
    handleSubmit,
    setValue,
    trigger,
  } = rhForm
  // this is to trigger validation for when the user addes an attachment and the form needs to validate the existing of [Attachment] in the body
  useEffect(() => {
    trigger('body')
  }, [getValues('attachments')])

  const onSubmit = (data) => {
    // THIS MEANS, THE 3 SECONDS AFTER THE SAVE ACTION HAVEN'T PASSED YET
    if (submittingStatus === SUBMITTING_STATUS.SUCCESS) {
      return;
    }

    setSubmittingStatus(SUBMITTING_STATUS.SUBMITTING);

    const formattedLabelValues = getFormattedLabelValues(rhForm, customFields);

    const payload = {
      title: data.title,
      subject: data.subject,
      cc: data.cc || [],
      bcc: data.bcc || [],
      body: data.body,
      type: data.type,
      customFilterValues: formattedLabelValues,
      associatedFiles: latestEmailTemplateORM.relations.associatedFiles.map(afORM =>
        afORM.model,
      ),
    };

    if (emailTemplateORM) {
      const isSavingDraft = emailTemplateORM?.model.status === EmailTemplateStatus.DRAFT &&
        performingAction.current === PERFORMING_ACTION.SAVE_EXISTING;
      const isReactivating = emailTemplateORM?.model.status === EmailTemplateStatus.DEACTIVATED &&
        performingAction.current === PERFORMING_ACTION.ACTIVATING;

      dispatch(emailTemplateActions.batchSave({
        model: EmailTemplate,
        entity: emailTemplateORM?.model,
        updates: {
          ...payload,
          status: isSavingDraft ? EmailTemplateStatus.DRAFT : EmailTemplateStatus.ACTIVE,
        },
      }))

      onComplete(emailTemplateORM?.model, isReactivating)
    } else {
      dispatch(emailTemplateActions.create({
        ...payload,
        status: performingAction.current === PERFORMING_ACTION.FIRST_DRAFT
          ? EmailTemplateStatus.DRAFT
          : EmailTemplateStatus.ACTIVE,
        callback: onComplete,
      }));
    }

    function onComplete(emailTemplateResponse: EmailTemplate, isReactivating?: boolean): void {
      let showToast = false;
      let toastMsg = '';

      if (performingAction.current === PERFORMING_ACTION.ACTIVATING) {
        showToast = true;
        toastMsg = isReactivating
          ? 'Email template reactivated'
          : 'Email template activated';
      }

      if (performingAction.current === PERFORMING_ACTION.SAVE_EXISTING &&
        emailTemplateORM?.model.status === EmailTemplateStatus.ACTIVE) {
        showToast = true;
        toastMsg = 'Email template updated'
      }

      if (showToast) {
        toast?.add(
          <GenericToast
            title={toastMsg}
            status="success"
          />,
          ToastOrientations.TOP_RIGHT,
        );

        props.toggleDrawer();
        return;
      }

      if (performingAction.current === PERFORMING_ACTION.FIRST_DRAFT) {
        dispatch(drawerActions.toggle({
          entity: DRAWER_ENTITIES.EMAIL_TEMPLATE,
          entityId: emailTemplateResponse.id,
          keepOpen: true,
        }));
      }

      setSubmittingStatus(SUBMITTING_STATUS.SUCCESS);

      setTimeout(() => {
        setSubmittingStatus(SUBMITTING_STATUS.NONE);
      }, 3000);
    }
  }

  const [associatedFilesChanged, setAssociatedFilesChanged] = useState(false)

  // MEMOIZED COMPONENTS
  const FooterMemoized = useMemo(() =>
    (<EmailDrawerFooter
      onPreview={onPreview}
      closeDrawer={onCancel}
      isFormDirty={formState.isDirty || associatedFilesChanged}
      submittingStatus={submittingStatus}
      emailTemplate={emailTemplateORM?.model}
      canActivate={formState.isValid}
      onActivate={onActivate}
      onDelete={onDelete}
    />)
  , [emailTemplateORM,
    formState.isDirty,
    formState.isValid,
    submittingStatus,
    associatedFilesChanged,
    activeAssociatedFiles,
  ]);

  const HeaderMemoized = useMemo(() =>
    (<EmailDrawerHeader
      closeDrawer={onCancel}
    />), [formState.isDirty]);

  // USED TO CAPTURE BODY TEXT INPUT EVENT AND CURSOR POSITION
  useEffect(() => {
    const bodyInputReference = document.querySelector('#email-template-form-body-input');

    function updateCursorRefPosition(event): void {
      // @ts-ignore
      bodyInputEndCursor.current = event?.target?.selectionEnd;
      // @ts-ignore
      bodyInputStartCursor.current = event?.target?.selectionStart;
    }

    // UPDATE THE CURSOR'S POSITION UPON CLICKING INSIDE THE TEXTAREA
    bodyInputReference?.addEventListener('click', updateCursorRefPosition);

    // UPDATE THE CURSOR'S POSITION UPON TYPING
    bodyInputReference?.addEventListener('keyup', updateCursorRefPosition);

    // AS SOON AS THE USER PRESSES THE BUTTON, THE FOCUS IS LOST
    // BUT WE STILL WANT, AT THIS MOMENT, TO ADD THE TEXT WHERE
    // THE FOCUS WAS (OTHERWISE IT'LL GO TO THE END) SO WE KEEP TRACK
    // OF WHEN THE FOCUS WAS LOST
    bodyInputReference?.addEventListener('blur', () => {
      lostBodyInputFocusAt.current = (new Date()).getTime();
    });

    return () => {
      bodyInputReference?.removeEventListener('click', () => { });
      bodyInputReference?.removeEventListener('keyup', () => { });
      bodyInputReference?.removeEventListener('blur', () => { });
      bodyInputReference?.removeEventListener('focus', () => { });
    };
  });

  function onDelete(): void {
    if (!emailTemplateORM) return;

    const payload = {
      isVisible: true,
      allowBackdropCancel: false,
      component: () =>
        (<DNAEmailTemplateDeleteModal
          toast={toast}
          emailTemplate={emailTemplateORM.model}
          onDelete={props.toggleDrawer}
        />),
    };

    dispatch(DNAModalActions.setModal(payload));
  }

  const getEmailTemplateType = () => {
    const type = rhForm.watch('type')
    return type
  }

  async function onPreview(): Promise<void> {
    const values = getValues()
    const isHtmlTemplate = getEmailTemplateType() === EmailTemplateType.HTML
    const assocFilesInfo = activeAssociatedFiles
      .filter(af => af.meta.canBeSharedByMSL)
      .map(af => {
        let contentId = af.file?.id!
        if (af.model.type === 'DOCUMENT') {
          const [currentAssociatedDoc] = allDocumentORM
            .filter(currentDoc => af.file?.id! === currentDoc.model.id)
          contentId = currentAssociatedDoc != null
            ? currentAssociatedDoc.relations.version.latestPublishedDocumentVersionORM?.model.id!
            : contentId
        }
        return {
          title: getAssociatedFileTitle(af),
          contentId,
          sharedContentId: af.model.id,
          type: af.model.type === 'ATTACHED_FILE' ? ShareType.ATTACHED_FILE : ShareType.DOCUMENT_VERSION,
          customValues: [],
        }
      })

    const cc = Array.isArray(values.cc) ? isStringArray(values.cc) ? values.cc : [] : values.cc.split(',')
    const bcc = Array.isArray(values.bcc) ? isStringArray(values.bcc) ? values.bcc : [] : values.bcc.split(',')

    const body = !isHtmlTemplate ? values.body as unknown as string : BODY_HTML_TEMPLATE;
    if (isHtmlTemplate) {
      const html = values.body.toString()
      const type = 'text/html';
      const blob = new Blob([html], { type });
      const clipboardItem = new ClipboardItem({ [type]: blob });
      await navigator.clipboard.write([clipboardItem]);
    }

    emailTemplatePreview(
      toast,
      values.subject as unknown as string,
      body,
      cc,
      bcc,
      assocFilesInfo,
    );

    analytics?.track('EMAIL_PREVIEW', {
      action: 'EMAIL_PREVIEW',
      category: 'EMAIL',
      emailTemplateId: emailTemplateORM?.model.id,
    });
  }

  function onCancel(): void {
    // ONLY SHOW THE CONFIRMATION MODAL IS THERE IS AN UNSAVED MODIFICATION
    if (formState.isDirty) {
      const payload = {
        isVisible: true,
        allowBackdropCancel: false,
        component: () =>
          (<DNACommonConfirmation
            status="danger"
            cancelText="Cancel"
            descriptionText="You will lose all unsaved changes."
            confirmActionText="Close without saving"
            onConfirmAction={props.toggleDrawer}
            title="Unsaved changes"
          />),
      };

      dispatch(DNAModalActions.setModal(payload));
    } else {
      props.toggleDrawer();
    }
  }

  function onActivate(action: PERFORMING_ACTION): void {
    performingAction.current = action;

    function activate(): void {
      handleSubmit(onSubmit)();

      // need to check the analytics here
      const analyticsActionName = emailTemplateORM?.model.id
        ? (action === PERFORMING_ACTION.SAVE_EXISTING
          ? 'EMAIL_SAVED'
          : (emailTemplateORM?.model.status === EmailTemplateStatus.ACTIVE
            ? 'EMAIL_SAVED'
            : 'EMAIL_ACTIVATED'))
        : 'EMAIL_CREATED';
      analytics?.track(analyticsActionName, {
        action: analyticsActionName,
        category: 'EMAIL',
        emailTemplateId: emailTemplateORM?.model.id,
      });
    }

    const isFirstDraft = action !== PERFORMING_ACTION.ACTIVATING && !emailTemplateORM;
    const isUpdateNotActive = action === PERFORMING_ACTION.SAVE_EXISTING &&
      emailTemplateORM?.model.status !== EmailTemplateStatus.ACTIVE;
    if (isFirstDraft || isUpdateNotActive) {
      activate();
      return;
    }

    const isActive = emailTemplateORM?.model.status === EmailTemplateStatus.ACTIVE;
    const payload = {
      isVisible: true,
      allowBackdropCancel: false,
      component: () =>
        (<DNACommonConfirmation
          cancelText="Cancel"
          descriptionText={isActive
            ? 'The changes made to this template will go live.'
            : 'Once activated, this template will go live.'
          }
          confirmActionText={isActive ? 'Update' : 'Activate'}
          onConfirmAction={activate}
          title={isActive ? 'Update Template' : 'Confirm activation?'}
        />),
    };

    dispatch(DNAModalActions.setModal(payload));
  }

  const insertBrackets = (placeholder: string) => {
    const body = getValues('body')
    const wasTheInputFocused = (new Date()).getTime() - lostBodyInputFocusAt.current < 300;

    if (wasTheInputFocused) {
      const newBody = (body as unknown as string).substring(0, bodyInputStartCursor.current) +
        placeholder + (body as unknown as string).substring(bodyInputEndCursor.current);

      const cursorNewPosition = bodyInputStartCursor.current + placeholder.length;
      bodyInputStartCursor.current = cursorNewPosition;
      bodyInputEndCursor.current = cursorNewPosition;

      rhForm.setValue('body', newBody, {
        shouldValidate: true,
        shouldDirty: true,
      })

      // AFTER ADDING THE [Files] TO THE TEXT, WE NEED TO REFOCUS THE CURSOR AT THAT POINT
      // @ts-ignore
      document.querySelector('#email-template-form-body-input')?.focus();
      setTimeout(() => {
        // @ts-ignore
        // eslint-disable-next-line max-len
        document.querySelector('#email-template-form-body-input')?.setSelectionRange(cursorNewPosition, cursorNewPosition);
      }, 200);
    } else {
      rhForm.setValue('body', body + placeholder, {
        shouldValidate: true,
        shouldDirty: true,
      })
      const cursorNewPosition = body.length + placeholder.length;
      bodyInputStartCursor.current = cursorNewPosition;
      bodyInputEndCursor.current = cursorNewPosition;
      setTimeout(() => {
        // @ts-ignore
        document.querySelector('#email-template-form-body-input')?.focus();
      }, 100);
    }
  }

  const handleUploadStatusChange = (status: UploadStatus) => {
    const hasStartedProcessing = processingAttachedFiles === false && status === 'IN_PROGRESS'
    const hasFinishedProcessing = processingAttachedFiles === true && status !== 'IN_PROGRESS'

    if (hasStartedProcessing) {
      setProcessingAttachedFiles(true)
    }
    else if (hasFinishedProcessing) {
      setValue('attachments', 'true')
      setProcessingAttachedFiles(false)
    }
    if (status === UPLOAD_STATUS.ERROR) {
      setValue('attachments', 'false')
    }
  }

  const handleSearchBoxSelect = (parentDoc: EmailTemplateORM, linkedDoc: DocumentORM) => {
    analytics?.track('SEARCH_AF', {
      action: 'INPUT',
      category: 'SEARCH',
    });
    const newAssociatedFile = createAssociatedFile({
      attachmentId: linkedDoc.model.id,
      type: AssociatedFileType.DOCUMENT,
      status: AssociatedFileStatus.ACTIVE,
    })

    setLatestEmailTemplateORM({
      ...parentDoc,
      relations: {
        ...parentDoc.relations,
        associatedFiles: [
          ...parentDoc.relations.associatedFiles ?? [],
          getAssociatedFileORMFromFile(linkedDoc.model, newAssociatedFile, tenant),
        ],
      },
    })
    setAssociatedFilesChanged(true)
    setValue('attachments', 'true')
  }

  const handleAssociatedFileDelete = (emailTemplateORM: EmailTemplateORM, item: DocumentORM | AttachedFile) => {
    const itemId = item.type === 'DOCUMENT' ? item.model.id : item.id

    const updatedAssociatedFiles = emailTemplateORM
      .relations
      .associatedFiles
      ?.map(associated => {
        if (associated.file?.id === itemId) {
          return {
            ...associated,
            model: {
              ...associated.model,
              status: AssociatedFileStatus.DELETED,
            },
          }
        }

        return associated
      })

    setLatestEmailTemplateORM({
      ...emailTemplateORM,
      relations: {
        ...emailTemplateORM.relations,
        associatedFiles: [
          ...(updatedAssociatedFiles || []),
        ],
      },
    })

    const markAsChange = emailTemplateORM
      ? emailTemplateORM.relations.associatedFiles.some(af => af.model.attachmentId === itemId)
      : false
    setAssociatedFilesChanged(markAsChange)
    // if we have at least one assoicated file we should keep the attachment to true so the form validation validates the existance of [Attachment]
    const hasAssociatedFiles = updatedAssociatedFiles
      .some(associatedFile => associatedFile.model.status !== AssociatedFileStatus.DELETED)
    setValue('attachments', hasAssociatedFiles ? 'true' : 'false')
  }

  const handleFinishedUpload = (attachedFile: AttachedFile) => {
    const newAssociatedFile = createAssociatedFile({
      attachmentId: attachedFile.id,
      type: AssociatedFileType.ATTACHED_FILE,
      status: 'ACTIVE',
    })
    const newAssocFileORM: AssociatedFileORM = {
      model: newAssociatedFile,
      meta: {
        canBeSharedByMSL: true,
      },
      type: ORMTypes.ASSOCIATED_FILE,
      file: attachedFile,
      relations: {},
    }
    const updatedAssociatedFiles = [
      ...latestEmailTemplateORM.relations.associatedFiles ?? [],
      newAssocFileORM,
    ]

    setLatestEmailTemplateORM({
      ...latestEmailTemplateORM,
      relations: {
        ...latestEmailTemplateORM.relations,
        associatedFiles: [
          ...(updatedAssociatedFiles || []),
        ],
      },
    })
    setAssociatedFilesChanged(true)
  }

  const isHTMLTemplate = getEmailTemplateType() === EmailTemplateType.HTML

  return (
    <DNABox appearance="col" fill>
      { /* HEADER */}
      {HeaderMemoized}
      <DNADivider />
      { /* CONTENT */}
      <DNABox appearance="col" style={[styles.commonPadding, { overflow: 'scroll' }]} fill>

        <DNABox appearance="col" spacing="md">
          { /* CUSTOM FIELDS */}

          <ComposableField
            key={fields[0].fieldName}
            field={fields[0]}
            placeholder="Template Name"
          />
          {
              customFields?.length > 0 && customFields.map((field) => (
                <DNABox key={field.id} appearance="col" spacing="xs">
                  <ComposableField
                    key={field.fieldName}
                    field={field}
                  />
                </DNABox>
              ))
            }
        </DNABox>

        { /* TEMPLATE CONTENT */}
        <DNABox appearance="col" spacing="md" style={{ marginTop: 32 }}>
          <DNABox spacing="between">
            <DNAText status="subtle" bold>Template Content</DNAText>
            <Iffy is={!customFields.length}>
              <DNAText testID="email-template-required-fields" status="danger">* Required fields</DNAText>
            </Iffy>
          </DNABox>
          <DNABox appearance="col" spacing="xs">
            {/* Subject */}
            <ComposableField
              key={fields[1].fieldName}
              field={fields[1]}
            />
          </DNABox>
          <DNABox appearance="col" spacing="xs">
            {/* CC */}
            <ComposableField
              key={fields[2].fieldName}
              field={fields[2]}
              placeholder={EMAIL_PLACEHOLDER}
            />
          </DNABox>
          <DNABox appearance="col" spacing="xs">
            {/* BCC */}
            <ComposableField
              key={fields[3].fieldName}
              field={fields[3]}
              placeholder={EMAIL_PLACEHOLDER}
            />
          </DNABox>
          <DNABox appearance="col" spacing="xs">
            <DNABox spacing="between" alignY="center">
              <DNABox>
                <DNAText status="danger">* </DNAText>
                <DNAText c2 bold>BODY</DNAText>
              </DNABox>
              <DNABox alignY="center" spacing="xs">
                <TouchableOpacity onPress={() => insertBrackets(BODY_PLACEHOLDERS.files)}>
                  <DNAText testID="email-template-files-button" status="primary" b2>
                    [Files]
                  </DNAText>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => insertBrackets(BODY_PLACEHOLDERS.attachments)}>
                  <DNAText testID="email-template-attachments-button" status="primary" b2>
                    [Attachments]
                  </DNAText>
                </TouchableOpacity>
                <Iffy is={isHTMLTemplate}>
                  <TouchableOpacity onPress={() => insertBrackets(BODY_PLACEHOLDERS.linkUrl)}>
                    <DNAText testID="email-template-hub-link-button" status="primary" b2>
                      [Link URL]
                    </DNAText>
                  </TouchableOpacity>
                </Iffy>
                <HelpCircleTooltip popoverText={BRACKETS_HELP} />
              </DNABox>
            </DNABox>
            <DNABox appearance="col" spacing="xs">
              <Iffy is={isHtmlTemplateEnabled}>
                {/* Template Type */}
                <DNABox spacing="xs" alignY="center">
                  <ComposableField
                    key={fields[6].fieldName}
                    field={fields[6]}
                    hideRequiredLabel
                    showFieldTitle={false}
                  />
                </DNABox>
              </Iffy>
              <Controller
                name="body"
                control={rhForm.control}
                render={({ field: { onChange, value } }) => {
                  const onBlur = () => {
                    lostBodyInputFocusAt.current = (new Date()).getTime();
                  }
                  return (
                    <InputComponent
                      testID="email-template-form-body-input"
                      title=""
                      hideLabel={true}
                      hidePlaceholder
                      inputStyle={styles.inputStyle}
                      removeMarginPadding
                      required={true}
                      multiline={true}
                      numOfLines={6}
                      value={value as unknown as string}
                      onChangeText={onChange}
                      onBlur={onBlur}
                    />
                  )
                }}
              />
              <Iffy is={formState.errors.body}>
                <DNAText testID="email-template-files-required-text" status="danger">
                  {formState.errors.body?.message}
                </DNAText>
              </Iffy>
            </DNABox>
          </DNABox>
          <DNABox appearance="col" spacing="xs">
            <AssociatedFilesManager
              entityORM={latestEmailTemplateORM!}
              canLinkFiles={true}
              canUploadFiles={true}
              isDisabled={false}
              associatedFilesMode="EMAIL_ATTACHMENT"
              searching={searching}
              setSearching={setSearching}
              emptyMessage="No associated files"
              files={activeAssociatedFiles}
              onUploadStatusChange={handleUploadStatusChange}
              onSelect={handleSearchBoxSelect}
              onFinishedUpload={handleFinishedUpload}
              onDelete={handleAssociatedFileDelete}
              showTooltip={false}
            />
          </DNABox>
        </DNABox>
      </DNABox>
      <DNABox />
      {/* FOOTER */}
      {FooterMemoized}
    </DNABox>
  );
};

export default EmailTemplateBody
